/** GA dashboard date range and formatting helpers. */

export const GA_RANGE_7D = 7;
export const GA_RANGE_30D = 30;
export const GA_PRESET_7D = '7d';
export const GA_PRESET_30D = '30d';
export const GA_PRESET_CUSTOM = 'custom';

const EMPTY_DASHBOARD_ROW = {
  dau: 0,
  wau: 0,
  mau: 0,
  active1DayUsers: 0,
  active7DayUsers: 0,
  active28DayUsers: 0,
  totalUsers: 0,
  newUsers: 0,
  sessions: 0,
  engagedSessions: 0,
  eventCount: 0,
  keyEvents: 0,
  engagementSec: 0,
  averageSessionDuration: 0,
  bounceRate: 0,
  crashAffectedUsers: 0,
  crashFreeUsersRate: 0,
  dauPerMau: 0,
  dauPerWau: 0,
  wauPerMau: 0,
  eventCountPerUser: 0,
  eventValue: 0,
  eventsPerSession: 0,
  engagementRate: 0,
  screenPageViews: 0,
  screenPageViewsPerSession: 0,
  screenPageViewsPerUser: 0,
  scrolledUsers: 0,
  sessionsPerUser: 0,
  userKeyEventRate: 0,
  topEventName: '',
  topEventCount: 0,
};

export function getGaDateRange(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days + 1);
  return {
    from: start.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
  };
}

export function todayDateString() {
  return new Date().toISOString().slice(0, 10);
}

/** Inclusive UTC calendar dates ending at todayDateString(), oldest first. */
export function lastNUtcDates(days) {
  const n = Math.max(1, Number(days) || 1);
  const today = todayDateString();
  const end = new Date(`${today}T00:00:00Z`);
  const out = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(end);
    d.setUTCDate(d.getUTCDate() - i);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

/** Validate YYYY-MM-DD range for GA API (from <= to, both required). */
export function validateGaDateRange(from, to) {
  if (!from || !to) {
    return { valid: false, errorKey: 'dashboard.ga.dateRange.required' };
  }
  if (from > to) {
    return { valid: false, errorKey: 'dashboard.ga.dateRange.invalidOrder' };
  }
  return { valid: true, errorKey: null };
}

/** Fill missing dates with zero metrics for chart continuity. */
export function fillGaTimeseries(rows, fromStr, toStr) {
  const byDate = {};
  (rows || []).forEach((row) => {
    if (row?.date) byDate[row.date] = row;
  });

  const out = [];
  const start = new Date(`${fromStr}T00:00:00Z`);
  const end = new Date(`${toStr}T00:00:00Z`);
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    const dateStr = d.toISOString().slice(0, 10);
    const existing = byDate[dateStr];
    out.push(existing ? { ...EMPTY_DASHBOARD_ROW, ...existing, date: dateStr } : { date: dateStr, ...EMPTY_DASHBOARD_ROW });
  }
  return out;
}

/** Latest row by date (for KPI cards). */
export function getLatestGaRow(rows) {
  if (!rows?.length) return null;
  return [...rows].sort((a, b) => a.date.localeCompare(b.date)).at(-1);
}

export function sumGaField(rows, field) {
  return (rows || []).reduce((acc, row) => acc + (row[field] || 0), 0);
}

/** @deprecated use sumGaField(rows, 'sessions') */
export function sumGaSessions(rows) {
  return sumGaField(rows, 'sessions');
}

/** Format seconds as "1h 26m" or "22m 30s". */
export function formatEngagementDuration(totalSeconds) {
  const sec = Math.max(0, Math.round(totalSeconds || 0));
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const seconds = sec % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  return `${seconds}s`;
}

/** Format ratio 0–1 as percentage string. */
export function formatPercent(ratio, digits = 1) {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(digits)}%`;
}

/** Average engagement per active user for latest day. */
export function avgEngagementPerUser(latestRow) {
  if (!latestRow?.dau) return 0;
  return (latestRow.engagementSec || 0) / latestRow.dau;
}

/** Sort breakdown rows by metric descending. */
export function sortBreakdownByMetric(items, metricKey, topN = 7) {
  return [...(items || [])]
    .sort((a, b) => (b[metricKey] || 0) - (a[metricKey] || 0))
    .slice(0, topN);
}

/** Format chart date MM/DD */
export function formatChartDate(value) {
  if (!value || typeof value !== 'string') return value;
  const parts = value.slice(0, 10).split('-');
  if (parts.length !== 3) return value;
  return `${parts[1]}/${parts[2]}`;
}

export const darkChartTooltipProps = {
  contentStyle: {
    backgroundColor: 'rgb(var(--admin-panel))',
    border: '1px solid rgb(var(--admin-border))',
    borderRadius: 6,
    color: 'rgb(var(--admin-text))',
  },
  labelStyle: { color: 'rgb(var(--admin-text))' },
  itemStyle: { color: 'rgb(var(--admin-text))' },
};

/** Normalized chart container heights for Auth + GA panels. */
export const CHART_HEIGHT = {
  hero: 'h-48 xl:h-56',
  wide: 'h-72 xl:h-80',
  map: 'h-[28rem] xl:h-[36rem]',
  panel: 'h-40 xl:h-48',
  compact: 'h-36',
};

export const CHART_GRID_STROKE = 'rgb(var(--admin-border))';
export const CHART_AXIS_STROKE = 'rgb(var(--admin-secondary))';
export const CHART_PRIMARY = 'rgb(var(--admin-accent))';
